import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { FiscalDeclarationPrompt } from "@/components/features/notarial-requests/fiscal-declaration-prompt";
import { DetailRow, DetailRows } from "@/components/shared/DetailRows";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getMyFiscalDeclaration,
  professionalFiscalStatus,
  type ProfessionalFiscalStatus,
} from "@/services/professionalEarningsService";

/**
 * The professional's OWN fiscal declaration, on their profile.
 *
 * WHY IT BELONGS HERE. Until now this data could only be entered at two
 * moments neither of which a professional can return to: on the invitation
 * form, and inline when a quote was refused for want of it. A professional
 * whose RUT changed, or who simply wanted to check what was on file, had
 * nowhere to look — and the server refuses their bids on the strength of it.
 *
 * VIEW FIRST, EDIT ON REQUEST. Rendering the form unconditionally would make
 * a settled declaration look unsettled every time someone opened their
 * profile. The summary answers "is this right?" and the button answers "change
 * it".
 *
 * The editor is `FiscalDeclarationPrompt` — the SAME component the quote
 * refusal shows, rather than a second form. It prefills from the server and
 * submits the complete triple, which is what stops a partial edit wiping
 * fields: the PUT replaces rather than merges, and `razonSocial` is nulled
 * when blank. A second form here would have to re-learn that, and the first
 * version that forgot would delete people's legal names without an error.
 */

const STATUS_VARIANTS: Record<ProfessionalFiscalStatus, "success" | "softDanger" | "warning"> = {
  complete: "success",
  cannot_issue_cfe: "softDanger",
  not_declared: "warning",
};

/** Three-way: null is "never answered", which must not read as a no. */
function cfeAnswerKey(issuesCfe: boolean | null | undefined): string {
  if (issuesCfe == null) return "profile.fiscal.cfeUnanswered";
  return issuesCfe ? "profile.fiscal.cfeYes" : "profile.fiscal.cfeNo";
}

export function MyFiscalDeclarationCard(): JSX.Element {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["me", "professional", "fiscal-declaration"],
    queryFn: getMyFiscalDeclaration,
  });

  const status = professionalFiscalStatus(data);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t("profile.fiscal.title")}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : editing ? (
          <FiscalDeclarationPrompt
            submitLabel={t("profile.fiscal.save")}
            onSaved={() => {
              setEditing(false);
              void refetch();
            }}
          />
        ) : (
          <div className="space-y-4">
            <DetailRows>
              <DetailRow label={t("profile.fiscal.status")}>
                <StatusBadge variant={STATUS_VARIANTS[status]}>
                  {t(`profile.fiscal.status_${status}`)}
                </StatusBadge>
              </DetailRow>
              <DetailRow label={t("profile.fiscal.rut")}>
                {data?.rut?.trim() ? data.rut : "—"}
              </DetailRow>
              <DetailRow label={t("profile.fiscal.razonSocial")}>
                {data?.razonSocial?.trim() ? data.razonSocial : "—"}
              </DetailRow>
              <DetailRow label={t("profile.fiscal.issuesCfe")}>
                {t(cfeAnswerKey(data?.issuesCfe))}
              </DetailRow>
            </DetailRows>

            {status !== "complete" ? (
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                {t(
                  status === "cannot_issue_cfe"
                    ? "profile.fiscal.cannotIssueHint"
                    : "profile.fiscal.notDeclaredHint",
                )}
              </p>
            ) : null}

            {/* Right-aligned per rule 1a8a68a0. */}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setEditing(true)}>
                {t("profile.fiscal.edit")}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
